import React, { useState, useEffect } from 'react';
import { generateVideo } from '../services/geminiService';
import { VideoIcon } from '../components/icons';

type AspectRatio = '16:9' | '9:16';

const VideoGenerationView: React.FC = () => {
    const [prompt, setPrompt] = useState('');
    const [aspectRatio, setAspectRatio] = useState<AspectRatio>('16:9');
    const [generatedVideo, setGeneratedVideo] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [progressMessage, setProgressMessage] = useState('');
    const [hasApiKey, setHasApiKey] = useState(false);
    const [isCheckingKey, setIsCheckingKey] = useState(true);
    
    useEffect(() => {
        const checkKey = async () => {
            setIsCheckingKey(true);
            try {
                if (window.aistudio && typeof window.aistudio.hasSelectedApiKey === 'function') {
                    const result = await window.aistudio.hasSelectedApiKey();
                    setHasApiKey(result);
                } else {
                    console.warn('aistudio API not found. Assuming API key is available.');
                    setHasApiKey(true);
                }
            } catch (e) {
                console.error("Error checking for API key:", e);
                setHasApiKey(true); // Assume true to not block in case of error
            }
            setIsCheckingKey(false);
        };
        checkKey();
    }, []);

    const handleSelectKey = async () => {
        if (window.aistudio && typeof window.aistudio.openSelectKey === 'function') {
            await window.aistudio.openSelectKey();
            setHasApiKey(true);
            setError(null);
        } else {
            setError("Could not open API key selection dialog.");
        }
    };

    const handleGenerate = async () => {
        if (!prompt.trim()) {
            setError('Please enter a prompt.');
            return;
        }
        setIsLoading(true);
        setError(null);
        setGeneratedVideo(null);
        setProgressMessage('');

        try {
            const videoUrl = await generateVideo(prompt, aspectRatio, setProgressMessage);
            setGeneratedVideo(videoUrl);
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'An unknown error occurred.';
            setError(errorMessage);
            if(errorMessage.includes('API Key error')) {
                setHasApiKey(false); // Prompt user to re-select key
            }
        } finally {
            setIsLoading(false);
            setProgressMessage('');
        }
    };

    const renderApiKeyPrompt = () => (
        <div className="bg-base-200 p-6 rounded-lg text-center border border-yellow-500/50">
            <h3 className="font-bold text-lg text-yellow-400">API Key Required for Video Generation</h3>
            <p className="text-text-secondary my-3">The Veo video generation model requires you to select your own API key. This is a mandatory step before generating videos.</p>
            <p className="text-sm text-base-300 mb-4">For billing information, please visit <a href="https://ai.google.dev/gemini-api/docs/billing" target="_blank" rel="noopener noreferrer" className="underline text-brand-secondary">ai.google.dev/gemini-api/docs/billing</a>.</p>
            <button
                onClick={handleSelectKey}
                className="bg-yellow-500 hover:bg-yellow-600 text-white font-bold py-2 px-4 rounded-lg transition-colors"
            >
                Select API Key
            </button>
        </div>
    );

    return (
        <div className="p-4 md:p-6 max-w-4xl mx-auto h-full">
            <div className="text-center mb-8">
                <h2 className="text-2xl font-bold">Video Generation</h2>
                <p className="text-text-secondary mt-2">Describe a short video clip for your ad or social media post.</p>
            </div>

            {isCheckingKey ? <div className="text-center">Checking API key status...</div> : !hasApiKey ? renderApiKeyPrompt() : (
            <div className="space-y-4">
                <textarea
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    placeholder="e.g., An aerial shot of a vibrant, futuristic city with flying cars at sunset"
                    className="w-full bg-base-200 rounded-lg p-3 border border-base-300 focus:border-brand-primary focus:outline-none resize-none"
                    rows={3}
                />
                <div className="flex flex-col sm:flex-row items-center gap-4">
                    <div className="w-full sm:w-auto">
                        <label className="font-semibold text-sm mr-2">Aspect Ratio:</label>
                        <div className="inline-flex rounded-md shadow-sm bg-base-200 p-1">
                            <button onClick={() => setAspectRatio('16:9')} className={`px-4 py-2 text-sm rounded-md transition-colors ${aspectRatio === '16:9' ? 'bg-brand-primary text-white' : 'hover:bg-base-300'}`}>16:9 Landscape</button>
                            <button onClick={() => setAspectRatio('9:16')} className={`px-4 py-2 text-sm rounded-md transition-colors ${aspectRatio === '9:16' ? 'bg-brand-primary text-white' : 'hover:bg-base-300'}`}>9:16 Portrait</button>
                        </div>
                    </div>
                    <button
                        onClick={handleGenerate}
                        disabled={isLoading}
                        className="w-full sm:flex-1 flex items-center justify-center gap-2 bg-brand-primary hover:bg-brand-secondary text-white font-bold py-3 px-4 rounded-lg transition-colors duration-300 disabled:bg-base-300"
                    >
                        {isLoading ? 'Generating...' : 'Generate Video'}
                        {!isLoading && <VideoIcon className="w-5 h-5" />}
                    </button>
                </div>
            </div>
            )}


            {error && <p className="text-red-400 text-center mt-4">{error}</p>}

            <div className="mt-8 flex justify-center items-center">
                {isLoading && (
                    <div className="w-full max-w-md h-64 border-4 border-dashed rounded-lg flex flex-col items-center justify-center text-text-secondary border-base-300 text-center p-4">
                        <div className="w-12 h-12 border-4 border-dashed rounded-full animate-spin border-brand-primary mb-4"></div>
                        <p className="font-semibold">Creating your video...</p>
                        <p className="text-sm">{progressMessage}</p>
                    </div>
                )}
                {generatedVideo && (
                    <div className="bg-base-200 p-2 rounded-lg border border-base-300 w-full max-w-md">
                        <video src={generatedVideo} controls autoPlay loop className="w-full rounded-md" />
                    </div>
                )}
            </div>
        </div>
    );
};

export default VideoGenerationView;
